import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, query } from "../../lib/api";
import { formatAmount, formatDate, formatDateTime } from "../../lib/format";
import type { Balance, ListResponse, Payout } from "../../lib/types";
import {
  EmptyState,
  Mono,
  PageHeader,
  Pagination,
  Sheet,
  SheetHeader,
  Skeleton,
  StatusPill,
  Table,
  Td,
  Th,
  Tr,
} from "../../components/ui";

const PAGE_SIZE = 25;

export function Payouts() {
  const [offset, setOffset] = useState(0);

  const payouts = useQuery({
    queryKey: ["payouts", offset],
    queryFn: () =>
      api.get<ListResponse<Payout>>(`/api/dashboard/payouts${query({ limit: PAGE_SIZE, offset })}`),
  });

  const balances = useQuery({
    queryKey: ["balances"],
    queryFn: () =>
      api.get<{ balances: Balance[] | null; pending: Balance[] | null }>(
        "/api/dashboard/balances",
      ),
  });

  const rows = payouts.data?.data ?? [];
  const available = balances.data?.balances ?? [];
  // Money that has succeeded but has not cleared its settlement hold. Shown separately
  // because a merchant who sees one figure and receives a smaller payout distrusts both.
  const held = (balances.data?.pending ?? []).filter((b) => b.minor > 0);

  return (
    <>
      <PageHeader
        eyebrow="Money"
        title="Payouts"
        description="Settlements from your PayFlow balance into your bank account."
      />

      <div className="mb-6 grid gap-6 lg:grid-cols-[1fr_2fr]">
        <Sheet className="settle settle-1 overflow-hidden">
          <SheetHeader title="Awaiting payout" description="Cleared, and in your next payout run" />
          {available.length === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-ink-muted">
              Nothing is waiting to be settled.
            </p>
          ) : (
            <ul className="divide-y divide-rule">
              {available.map((balance) => (
                <li key={balance.currency} className="flex items-baseline justify-between px-5 py-3.5">
                  <span className="font-mono text-[12px] text-ink-muted">{balance.currency}</span>
                  <span className="font-display text-[22px] tabular text-verdigris">
                    {formatAmount(balance.minor)}
                  </span>
                </li>
              ))}
            </ul>
          )}
          {held.length > 0 && (
            <div className="border-t border-rule px-5 py-3.5">
              <p className="label mb-2">Still clearing</p>
              <ul className="grid gap-1.5">
                {held.map((balance) => (
                  <li key={balance.currency} className="flex items-baseline justify-between">
                    <span className="font-mono text-[11.5px] text-ink-muted">{balance.currency}</span>
                    <span className="tabular text-[15px] text-ink-muted">
                      {formatAmount(balance.minor)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div className="border-t border-rule px-5 py-3.5">
            <p className="text-[12px] leading-relaxed text-ink-muted">
              Payments succeed immediately but are held briefly before they can be paid
              out. The figure above is what your next run will actually move.
            </p>
          </div>
        </Sheet>

        <Sheet className="settle settle-2 overflow-hidden">
          <SheetHeader title="How settlement works" />
          <ol className="space-y-3.5 px-5 py-4">
            {[
              ["A customer pays", "The bank confirms it, and we credit your PayFlow balance less the fees."],
              ["The hold period passes", "Funds clear the settlement hold on your account, usually the next business day."],
              ["We pay out", "Cleared funds are transferred to your verified bank account and marked paid here."],
            ].map(([title, body], i) => (
              <li key={i} className="flex gap-3">
                <span className="mt-px flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-full border border-rule-strong font-mono text-[10px]">
                  {i + 1}
                </span>
                <div>
                  <p className="text-[13px] font-medium text-ink">{title}</p>
                  <p className="mt-0.5 text-[12.5px] leading-relaxed text-ink-muted">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </Sheet>
      </div>

      <Sheet className="settle settle-3 overflow-hidden">
        <SheetHeader title="Payout history" />
        {payouts.isLoading ? (
          <Skeleton />
        ) : rows.length === 0 ? (
          <EmptyState
            title="No payouts yet"
            description="Once you have cleared funds, settlements will be listed here with their bank references."
          />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Payout</Th>
                  <Th>Status</Th>
                  <Th>Period</Th>
                  <Th align="right">Payments</Th>
                  <Th align="right">Amount</Th>
                  <Th>Bank reference</Th>
                  <Th align="right">Paid</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((payout) => (
                  <Tr key={payout.id}>
                    <Td mono>
                      <span className="text-ink">{payout.id.slice(0, 12)}…</span>
                      <span className="mt-0.5 block text-[11px] text-ink-faint">
                        {payout.destination.bank_name} {payout.destination.account_number_masked}
                      </span>
                    </Td>
                    <Td>
                      <StatusPill status={payout.status} />
                      {payout.failure_reason && (
                        <span className="mt-1 block max-w-[200px] text-[11px] text-sienna">
                          {payout.failure_reason}
                        </span>
                      )}
                    </Td>
                    <Td className="whitespace-nowrap text-[12px] text-ink-muted">
                      {formatDate(payout.period_start)} — {formatDate(payout.period_end)}
                    </Td>
                    <Td align="right" className="tabular">
                      {payout.payment_count}
                    </Td>
                    <Td align="right" mono className="font-medium">
                      {payout.amount} <span className="text-ink-faint">{payout.currency}</span>
                    </Td>
                    <Td>{payout.reference ? <Mono copyable>{payout.reference}</Mono> : "—"}</Td>
                    <Td align="right" className="whitespace-nowrap text-[12px] text-ink-muted">
                      {payout.paid_at ? formatDateTime(payout.paid_at) : "—"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination
              total={payouts.data?.total ?? 0}
              limit={PAGE_SIZE}
              offset={offset}
              onChange={setOffset}
            />
          </>
        )}
      </Sheet>
    </>
  );
}
